import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { Field, Select, formatDate, todayIso } from '@/components/live';
import { callRpc } from '@/api/resources';
import { plotOptions } from '@/api/lookups';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';


/** What a request asks the satellite to do; each becomes a check of the plots over the dates chosen. */
const REQUEST_KINDS = [
  { id: 'recheck', label: 'Check for change over these dates' },
  { id: 'investigation', label: 'Investigate a reported issue' },
  { id: 'backfill', label: 'Look back at history' },
];
/** Sentinel-2 imagery, as the Copernicus archive holds it, starts in mid-2015. */
const EARLIEST = '2015-07-01';
const daysBefore = (iso: string, days: number) => new Date(new Date(iso).getTime() - days * 86400000).toISOString().slice(0, 10);

/**
 * Ask the satellite (Copernicus Sentinel-2) to look at plots now, over the dates chosen (owner,
 * 2026-09-28: "add a request monitoring button ... and date selection"; kept on the Satellite page,
 * not the plot, because "plots are static, and one time activity. once plot is locked i dont want to
 * edit again"). One request is a batch; the worker picks it up within about 15 minutes and the
 * results are on the Requests & results tab, and on each plot's timeline. A plot not yet monitored is enrolled first - it needs an
 * approved boundary, and one without is reported as skipped.
 */
export function RequestMonitoring({ monitoredPlotIds, onRequested }: { monitoredPlotIds: string[]; onRequested?: () => void }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const mayRequest = can(profile, 'plot.monitoring', 'update');
  const today = todayIso();
  const [plots, setPlots] = useState<string[]>([]);
  const [kind, setKind] = useState('recheck');
  const [from, setFrom] = useState(daysBefore(today, 30));
  const [to, setTo] = useState(today);
  const [note, setNote] = useState('');
  // A picture on the same day of every month (owner, 2026-09-28: "Jan 10 to Sep 10. Every month 10th date").
  const [monthly, setMonthly] = useState(false);
  const [seriesDay, setSeriesDay] = useState('10');
  const [busy, setBusy] = useState(false);
  const options = useQuery({ queryKey: ['picker', 'plots', 'with-farmer'], queryFn: () => plotOptions(), enabled: isSupabaseConfigured && mayRequest });
  // Monitored plots first: they are the ones a request can always reach.
  const choices = useMemo(() => [...(options.data ?? [])].sort((a, b) =>
    Number(monitoredPlotIds.includes(b.id)) - Number(monitoredPlotIds.includes(a.id)) || a.label.localeCompare(b.label)), [options.data, monitoredPlotIds]);

  const problem = plots.length === 0 ? 'Choose at least one plot.'
    : !from || !to ? 'Choose both dates.'
    : from > to ? 'The start date is after the end date.'
    : to > today ? 'The end date cannot be in the future.'
    : from < EARLIEST ? 'Satellite imagery starts in July 2015.'
    : (new Date(to).getTime() - new Date(from).getTime()) / 86400000 > 366 ? 'Choose at most a year at a time.'
    : monthly && !(Number(seriesDay) >= 1 && Number(seriesDay) <= 28) ? 'The day of the month is 1 to 28.'
    : null;

  async function request() {
    if (problem) { toast.error(problem); return; }
    setBusy(true);
    try {
      const result = await callRpc<{ queued: number; skipped: number }>('fn_request_satellite_monitoring', {
        p_plot_ids: plots, p_batch_type: kind, p_window_from: from, p_window_to: to, p_note: note.trim() || null,
        p_series_day: monthly ? Number(seriesDay) : null,
      });
      if (result.queued > 0) {
        toast.success(`Requested for ${result.queued} plot${result.queued === 1 ? '' : 's'}, ${formatDate(from)} to ${formatDate(to)}; it runs within about 15 minutes.`
          + (result.skipped ? ` ${result.skipped} skipped: no approved boundary.` : ''));
      } else {
        toast.error('Nothing requested: the plots chosen have no approved boundary yet.');
      }
      await queryClient.invalidateQueries({ queryKey: ['satellite_jobs'] });
      await queryClient.invalidateQueries({ queryKey: ['satellite_monitoring'] });
      await queryClient.invalidateQueries({ queryKey: ['satellite_batches'] });
      setPlots([]);
      setNote('');
      if (result.queued > 0) onRequested?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The request could not be made.');
    } finally {
      setBusy(false);
    }
  }

  const allMonitored = monitoredPlotIds.length > 0 && monitoredPlotIds.every((id) => plots.includes(id));
  return (
    <section className="card">
      <div className="section-heading"><h3><Send size={15} /> Request monitoring</h3></div>
      {mayRequest ? (
        <>
          <Field hint="Monitored plots are listed first. A plot needs an approved boundary." label={`Plots (${plots.length} chosen)`}>
            <div className="check-list is-stacked">
              {monitoredPlotIds.length > 0 && (
                <label>
                  <input checked={allMonitored} onChange={() => setPlots(allMonitored ? [] : [...new Set([...plots, ...monitoredPlotIds])])} type="checkbox" />
                  <strong>All monitored plots ({monitoredPlotIds.length})</strong>
                </label>
              )}
              {choices.map((row) => (
                <label key={row.id}>
                  <input checked={plots.includes(row.id)} type="checkbox"
                         onChange={() => setPlots(plots.includes(row.id) ? plots.filter((id) => id !== row.id) : [...plots, row.id])} />
                  <span>{row.label}{monitoredPlotIds.includes(row.id) ? '' : <small className="muted"> - not monitored yet</small>}</span>
                </label>
              ))}
            </div>
          </Field>
          <div className="field-row">
            <Field label="Check">
              <Select onChange={setKind} options={REQUEST_KINDS} value={kind} />
            </Field>
            <Field label="From">
              <input max={to || today} min={EARLIEST} onChange={(event) => setFrom(event.target.value)} type="date" value={from} />
            </Field>
            <Field label="To">
              <input max={today} min={from || EARLIEST} onChange={(event) => setTo(event.target.value)} type="date" value={to} />
            </Field>
          </div>
          <Field hint="The clearest scene within a week of that day, each month in the dates - to see the season and any change month by month."
                 label="Monthly pictures">
            <label className="check-inline">
              <input checked={monthly} onChange={(event) => setMonthly(event.target.checked)} type="checkbox" />
              <span>A picture every month on day</span>
              <input aria-label="Day of the month" disabled={!monthly} inputMode="numeric" maxLength={2} onChange={(event) => setSeriesDay(event.target.value.replace(/\D/g, ''))}
                     style={{ width: 56 }} value={seriesDay} />
            </label>
          </Field>
          <Field hint="Why the check is asked for: a complaint, a reported felling, an audit query." label="Note">
            <input onChange={(event) => setNote(event.target.value)} value={note} />
          </Field>
          <div className="inline-form-actions">
            {problem && plots.length > 0 && <small className="field-error">{problem}</small>}
            <button className="primary" disabled={busy || Boolean(problem)} onClick={() => void request()} title={problem ?? undefined} type="button">
              <Send size={14} /> {busy ? 'Requesting...' : 'Request monitoring'}
            </button>
          </div>
        </>
      ) : <p className="muted">Your role cannot request satellite checks.</p>}
    </section>
  );
}
